import {
    Alert,
    Box,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import {Close, Straighten, TextFields} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import PreviewDropTableElementDesktop from "./PreviewDropTableElementDesktop";
import {OctagonShapeIcon} from "../shared/OctagonShapeIcon";
import {CircleTableIcon} from "../shared/CircleTableIcon";
import {RectangleTableIcon} from "../shared/RectangleTableIcon";
import {RectangleIcon} from "../shared/RectangleIcon";
import {CircleShapeIcon} from "../shared/CircleShapeIcon";
import {EllipseShapeIcon} from "../shared/EllipseShapeIcon";
import {TriangleShapeIcon} from "../shared/TriangleShapeIcon";
import {HexagonShapeIcon} from "../shared/HexagonShapeIcon";

interface Props {
    onClick: () => void
}

const AddPanel: React.FC<Props> = ({onClick}) => {
    const {t} = useTranslation();

    return <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: 1, borderColor: 'divider'}}>
            <Stack direction="row" alignItems="center"
                   justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6">{t('Add Elements')}</Typography>
                </Stack>
                <Tooltip title={t('Close')}>
                    <IconButton size="small"
                                onClick={onClick}>
                        <Close fontSize="small"/>
                    </IconButton>
                </Tooltip>
            </Stack>
        </Box>

        <Box sx={{flex: 1, overflow: 'auto', p: 2}}>
            <Alert severity="info" sx={{mb: 2}}>
                {t('Drag elements onto the canvas')}
            </Alert>

            <Stack spacing={2}>
                <Box>
                    <Typography variant="overline" color="text.secondary"
                                sx={{mb: 1, display: 'block'}}>
                        {t('Tables')}
                    </Typography>
                    <Stack spacing={1}>
                        <PreviewDropTableElementDesktop
                            type="table-circle"
                            icon={<CircleTableIcon/>}
                            label={t('Circle Table')}
                            description={t('Round table with seats')}
                        />
                        <PreviewDropTableElementDesktop
                            type="table-rectangle"
                            icon={<RectangleTableIcon/>}
                            label={t('Rectangle Table')}
                            description={t('Rectangular table with seats')}
                        />
                    </Stack>
                </Box>

                <Divider/>

                <Box>
                    <Typography variant="overline" color="text.secondary"
                                sx={{mb: 1, display: 'block'}}>
                        {t('Walls')}
                    </Typography>
                    <Stack spacing={1}>
                        <PreviewDropTableElementDesktop
                            type="wall-vertical"
                            icon={<Straighten
                                sx={{transform: 'rotate(90deg)'}}/>}
                            label={t('Vertical Wall')}
                            description={t('Add a vertical wall')}
                        />
                        <PreviewDropTableElementDesktop
                            type="wall-horizontal"
                            icon={<Straighten/>}
                            label={t('Horizontal Wall')}
                            description={t('Add a horizontal wall')}
                        />
                    </Stack>
                </Box>

                <Divider/>

                <Box>
                    <Typography variant="overline" color="text.secondary"
                                sx={{mb: 1, display: 'block'}}>
                        {t('Elements')}
                    </Typography>
                    <Stack spacing={1}>
                        <PreviewDropTableElementDesktop
                            type="shape-rect"
                            icon={<RectangleIcon/>}
                            label={t('Rectangle')}
                            description={t('Basic rectangle shape')}
                        />
                        <PreviewDropTableElementDesktop
                            type="shape-circle"
                            icon={<CircleShapeIcon/>}
                            label={t('Circle')}
                            description={t('Basic circle shape')}
                        />
                        <PreviewDropTableElementDesktop
                            type="shape-ellipse"
                            icon={<EllipseShapeIcon/>}
                            label={t('Ellipse')}
                            description={t('Oval shape')}
                        />
                        <PreviewDropTableElementDesktop
                            type="shape-triangle"
                            icon={<TriangleShapeIcon/>}
                            label={t('Triangle')}
                            description={t('Three-sided shape')}
                        />
                        <PreviewDropTableElementDesktop
                            type="shape-hexagon"
                            icon={<HexagonShapeIcon/>}
                            label={t('Hexagon')}
                            description={t('Six-sided polygon')}
                        />
                        <PreviewDropTableElementDesktop
                            type="shape-octagon"
                            icon={<OctagonShapeIcon/>}
                            label={t('Octagon')}
                            description={t('Eight-sided polygon')}
                        />
                        <PreviewDropTableElementDesktop
                            type="text"
                            icon={<TextFields/>}
                            label={t('Text Label')}
                            description={t('Add text annotation')}
                        />
                    </Stack>
                </Box>
            </Stack>
        </Box>
    </Box>
}

export default AddPanel;